// @ts-strict-ignore
import React, { useContext, useMemo } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import type { TabItem } from '@usealloy/component-library';
import { Box, Tabs } from '@usealloy/component-library';

import { UserContext } from '~/dashboard/context';
import { getAllTableColumns, reviewTableColumns } from '../../constants';
import { ReviewTabCount, TableTabWrapper } from '../../styles';
import type {
  MatchData,
  FilterOptions,
  FilterState,
  SetFilterState,
  TabOptions,
} from '../../types';
import { calculateAvailableColumns, calculateWatchlistTableProps } from './helpers';
import WatchlistFilters from './WatchlistFilters';
import WatchlistTable from './WatchlistTable';

interface WatchlistTableViewProps {
  allFilters: FilterState;
  allMatches: MatchData[];
  enableResolution: boolean;
  filteredAllMatches: MatchData[];
  filterOptions: FilterOptions;
  filteredReviewMatches: MatchData[];
  onRowClick: (match: MatchData) => void;
  openModal(matches: MatchData[]): void;
  reviewFilters: FilterState;
  selectedRows: any;
  setSelectedRows: any;
  setAllFilters: SetFilterState;
  setReviewFilters: SetFilterState;
  setTab: (tab: TabOptions) => void;
  tab: TabOptions;
  totalReviewCount: number;
}

const WatchlistTableView = ({
  allFilters,
  allMatches,
  enableResolution,
  filteredAllMatches,
  filterOptions,
  filteredReviewMatches,
  onRowClick,
  openModal,
  reviewFilters,
  selectedRows,
  setSelectedRows,
  setAllFilters,
  setReviewFilters,
  setTab,
  tab,
  totalReviewCount,
}: WatchlistTableViewProps) => {
  const { featureFlags } = useContext(UserContext);
  const showAiAssisted = Boolean(featureFlags?.llmWatchlistAgent);

  const {
    disabledReviewTab,
  } = useMemo(() => calculateWatchlistTableProps(allMatches),
    [allMatches],
  );

  const {
    availableFiltersReviewTab,
    columnsReviewTab,
    availableFiltersAllTab,
    columnsAllTab,
  } = useMemo(() => {
    const {
      availableFilters: availableFiltersReview,
      availableColumns: colsReviewTab,
    } = calculateAvailableColumns('review', reviewTableColumns, filteredReviewMatches);

    const {
      availableFilters: availableFiltersAll,
      availableColumns: colsAllTab,
    } = calculateAvailableColumns(
      'all',
      getAllTableColumns(showAiAssisted),
      filteredAllMatches,
      {
        isSuppressed: (value: any) => value,
        note: (value: any) => typeof value === 'string' && value.length > 0,
      },
    );

    return {
      availableFiltersReviewTab: availableFiltersReview,
      columnsReviewTab: colsReviewTab,
      availableFiltersAllTab: availableFiltersAll,
      columnsAllTab: colsAllTab,
    };
  }, [filteredAllMatches, filteredReviewMatches, showAiAssisted]);

  return (
    <Tabs
      onValueChange={(value: TabOptions) => {
        datadogRum.addAction(`watchlistMatches-tab-${value}`);
        setTab(value);
      }}
      style={{ width: '100%' }}
      tabs={[
        {
          disabled: disabledReviewTab,
          content: (
            <TableTabWrapper direction="column">
              <WatchlistFilters
                filterOptions={filterOptions}
                filterState={reviewFilters}
                needsReview
                setFilters={setReviewFilters}
                availableFilters={availableFiltersReviewTab}
              />
              <WatchlistTable
                columns={columnsReviewTab}
                enableResolution={enableResolution}
                filterOptions={filterOptions}
                filteredMatches={filteredReviewMatches}
                onRowClick={onRowClick}
                openModal={openModal}
                selectedRows={selectedRows}
                setSelectedRows={setSelectedRows}
              />
            </TableTabWrapper>
          ),
          label: (
            <Box direction="row" gap={1} id="watchlist-needs-review-tab">
              Needs Review
              {totalReviewCount > 0 && (
                <ReviewTabCount status="danger">
                  <Box justifyContent="center" alignItems="center">
                    {totalReviewCount}
                  </Box>
                </ReviewTabCount>
              )}
            </Box>
          ),
          value: 'review',
        },
        {
          content: (
            <TableTabWrapper direction="column">
              <WatchlistFilters
                filterOptions={filterOptions}
                filterState={allFilters}
                setFilters={setAllFilters}
                availableFilters={availableFiltersAllTab}
              />
              <WatchlistTable
                columns={columnsAllTab}
                filterOptions={filterOptions}
                filteredMatches={filteredAllMatches}
                onRowClick={onRowClick}
                openModal={openModal}
                selectedRows={selectedRows}
                setSelectedRows={setSelectedRows}
              />
            </TableTabWrapper>
          ),
          label: 'All',
          value: 'all',
        },
      ].filter((item: TabItem) => !item.disabled)}
      value={tab}
    />
  );
};

export default WatchlistTableView;
